import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

function schoolParams(params: Record<string, string | number | null | undefined> = {}) {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
  })
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export const schoolAdminApi = createApi({
  reducerPath: 'schoolAdminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/school',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
  endpoints: (builder) => ({
    getSchools: builder.query<any, void>({
      query: () => '/schools',
      transformResponse: (r: any) => r.data,
    }),
    getSchoolOverview: builder.query<any, Record<string, any>>({
      query: (params) => `/overview${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolTeachers: builder.query<any, Record<string, any>>({
      query: (params) => `/teachers${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolTeacher: builder.query<any, { userId: number } & Record<string, any>>({
      query: ({ userId, ...params }) => `/teachers/${userId}${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolStudents: builder.query<any, Record<string, any>>({
      query: (params) => `/students${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolStudent: builder.query<any, { userId: number } & Record<string, any>>({
      query: ({ userId, ...params }) => `/students/${userId}${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolClasses: builder.query<any, Record<string, any>>({
      query: (params) => `/classes${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolClass: builder.query<any, { projectId: number } & Record<string, any>>({
      query: ({ projectId, ...params }) => `/classes/${projectId}${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolUnit: builder.query<any, { requestId: number } & Record<string, any>>({
      query: ({ requestId, ...params }) => `/units/${requestId}${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolActivity: builder.query<any, { ticketId: number } & Record<string, any>>({
      query: ({ ticketId, ...params }) => `/activities/${ticketId}${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
    getSchoolReports: builder.query<any, Record<string, any>>({
      query: (params) => `/reports${schoolParams(params)}`,
      transformResponse: (r: any) => r.data,
    }),
  }),
})

export const {
  useGetSchoolsQuery,
  useGetSchoolOverviewQuery,
  useGetSchoolTeachersQuery,
  useGetSchoolTeacherQuery,
  useGetSchoolStudentsQuery,
  useGetSchoolStudentQuery,
  useGetSchoolClassesQuery,
  useGetSchoolClassQuery,
  useGetSchoolUnitQuery,
  useGetSchoolActivityQuery,
  useGetSchoolReportsQuery,
} = schoolAdminApi
